import React, { useState, useEffect, useRef } from 'react'
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  FormControlLabel,
  Tabs,
  Tab,
  Box,
  CircularProgress,
} from '@mui/material'
import { InviteUserFormData, AVAILABLE_PERMISSIONS } from '../../types'
import type { CompanyUser } from '../../../../core/models/Company'
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  StyledButton,
  StyledSwitch,
  StyledFormControlLabel,
} from '../../styles/StyledComponents'
import { Add as AddIcon, Link as LinkIcon, Email as EmailIcon, Person as PersonIcon, CheckCircle as CheckCircleIcon, Error as ErrorIcon } from '@mui/icons-material'
import { UserSearchAutocomplete } from '../UserSearchAutocomplete'
import WorkspaceInviteLinkCard from '../WorkspaceInviteLinkCard'
import { CompanyService } from '../../../../services/CompanyService'
import { trackEvent } from '../../../../services/analyticsService'
import { alpha, useTheme } from '@mui/material/styles'
import { useActorPermissionKeys, useSurface } from '../../../../hooks/usePermission'
import { useInviteRoles } from '../../../../hooks/useInviteRoles'
import {
  assignableRoles,
  effectiveDefaultRoleKey,
  memberRoleLabel,
  roleNameOf,
  roleSendValue,
  roleWithoutPicker,
} from '../../utils/roleOptions'

interface InviteUserDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: () => void
  formData: InviteUserFormData
  onFormChange: (formData: InviteUserFormData) => void
  companyId?: string
  /** The company's default role, sent for an inviter who may not pick a role (no `roles.assign`). */
  companyDefaultRole?: string
  /** `settings.default_role_key`: used only when the roles API does not send `defaultRoleKey`. */
  companyDefaultRoleKey?: string
  isGlobalAdmin?: boolean
  /**
   * Adds the "Invite link" tab (the company's join link) for people who hold members.invite. Only
   * the company manager view turns it on: the platform admin view keeps its two tabs.
   */
  showInviteLink?: boolean
  /**
   * The company's roster, when the parent already holds it (no request is made for it here). An
   * address that matches a member is a re-invite: it keeps the member's role unless the inviter
   * picks another. Left out, every address is treated as a new person.
   */
  members?: readonly Pick<CompanyUser, 'role' | 'roleKey' | 'role_name' | 'user'>[]
}

// Third tab, after "Invite by Email" (0) and "Add Existing User" (1).
const LINK_TAB = 2

function findMember(
  members: InviteUserDialogProps['members'],
  email: string | undefined
) {
  const wanted = (email ?? '').trim().toLowerCase()
  if (!wanted || !members) return undefined
  return members.find((m) => m.user?.email?.trim().toLowerCase() === wanted)
}

export const InviteUserDialog: React.FC<InviteUserDialogProps> = ({
  open,
  onClose,
  onSubmit,
  formData,
  onFormChange,
  companyId,
  companyDefaultRole,
  companyDefaultRoleKey,
  isGlobalAdmin = false,
  showInviteLink = false,
  members,
}) => {
  const theme = useTheme()
  // Without roles.assign the picker is hidden and the invite carries the company default (SC-439).
  const mayAssignRoles = useSurface('invite-dialog.role-picker')
  const actorPermissions = useActorPermissionKeys()
  // The link lets anyone join, so the tab follows the card's own surface (members.invite), not
  // a role name; the cloud checks again.
  const mayShowInviteLink = useSurface('workspace-invite-link.card')
  const showLinkTab = showInviteLink && mayShowInviteLink && !!companyId
  // One roles request each time the dialog opens: names, the default role and what this person
  // may pick all come from it. If it fails the invite carries the default role, with no picker.
  const rolesState = useInviteRoles(companyId, open)
  const rolesReady = rolesState.status === 'ready'
  // The roles list failing doesn't hide the company's own default: the settings the parent holds
  // say it, so a custom default is still what the invite carries (only the picker needs the list).
  const defaultRoleKey = effectiveDefaultRoleKey({
    defaultRoleKey: rolesReady ? rolesState.defaultRoleKey : undefined,
    settingsRoleKey: companyDefaultRoleKey,
    legacyRole: companyDefaultRole,
  })
  const choices = rolesReady ? assignableRoles(rolesState.roles, actorPermissions, defaultRoleKey) : []
  const canPickRole = mayAssignRoles && choices.length > 0
  // A pick is kept apart from the parent's form: the form can carry a role from an earlier open,
  // and an untouched picker must show (and send) the company default.
  const [pickedRoleKey, setPickedRoleKey] = useState<string | null>(null)
  const pickedRole = choices.find((r) => r.key === pickedRoleKey)
  const selectedRoleKey = pickedRole?.key ?? defaultRoleKey
  // Re-inviting a member is not a demotion to the default: without an explicit pick the invite
  // names no role and cloud leaves theirs alone.
  const knownMember = formData.invitationType !== 'existing_user' ? findMember(members, formData.email) : undefined
  const inviteRole: string | undefined = knownMember
    ? pickedRole
      ? roleSendValue(pickedRole.key)
      : undefined
    : canPickRole && selectedRoleKey !== defaultRoleKey
      ? roleSendValue(selectedRoleKey)
      : roleWithoutPicker(companyDefaultRole, defaultRoleKey)
  const knownMemberRoleName = knownMember
    ? memberRoleLabel(knownMember, 'their current role', rolesState.roles)
    : undefined
  const defaultRoleName = roleNameOf(rolesState.roles, defaultRoleKey)
  const [pickedTab, setActiveTab] = useState(0)
  // A link tab that is no longer allowed (the permission went away) is never passed on, so there
  // is no frame with a bad Tabs value: it falls back to the invite tab the form is on.
  const activeTab =
    pickedTab === LINK_TAB && !showLinkTab
      ? formData.invitationType === 'existing_user'
        ? 1
        : 0
      : pickedTab
  const onLinkTab = activeTab === LINK_TAB
  // The address the server last judged: a round trip through the link tab must not ask again.
  const lastValidatedEmail = useRef<string | null>(null)
  const [emailValidation, setEmailValidation] = useState<{
    isValidating: boolean
    isValid: boolean | null
    message: string
    canInvite: boolean
    userExists: boolean | null
  }>({
    isValidating: false,
    isValid: null,
    message: '',
    canInvite: false,
    userExists: null
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Initialize form data with default values
  useEffect(() => {
    if (open && !formData.invitationType) {
      onFormChange({
        ...formData,
        invitationType: 'email',
        selectedUser: null,
      })
    }
  }, [open, formData.invitationType, onFormChange])

  // The existing-user path is sent by the parent from the form, so the form carries the role
  // this dialog would send.
  useEffect(() => {
    if (open && inviteRole !== undefined && formData.role !== inviteRole) {
      onFormChange({ ...formData, role: inviteRole })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, inviteRole])

  // Reset validation state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setPickedRoleKey(null)
      lastValidatedEmail.current = null
      setEmailValidation({
        isValidating: false,
        isValid: null,
        message: '',
        canInvite: false,
        userExists: null
      })
    }
  }, [open])

  // Reopening starts on an invite tab, not on the link. It is reset on open, not on close, so the
  // body does not jump to the form while the dialog fades out. Done while rendering so the card
  // never mounts (and fetches) for a frame on reopen.
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open && pickedTab === LINK_TAB) {
      setActiveTab(formData.invitationType === 'existing_user' ? 1 : 0)
    }
  }

  const handleInputChange = (field: keyof InviteUserFormData, value: any) => {
    onFormChange({
      ...formData,
      [field]: value,
    })
  }

  // Validate email when user types in email field
  const validateEmail = async (email: string) => {
    if (!email || !companyId) {
      setEmailValidation({
        isValidating: false,
        isValid: null,
        message: '',
        canInvite: false,
        userExists: null
      })
      return
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setEmailValidation({
        isValidating: false,
        isValid: false,
        message: 'Please enter a valid email address',
        canInvite: false,
        userExists: null
      })
      return
    }

    lastValidatedEmail.current = email
    setEmailValidation(prev => ({ ...prev, isValidating: true }))

    try {
      const result = await CompanyService.validateEmailForInvitation(email, companyId)
      
      if (result.success) {
        setEmailValidation({
          isValidating: false,
          isValid: true,
          message: result.message || 'Email is valid for invitation',
          canInvite: result.canInvite,
          userExists: result.userExists
        })
      } else {
        setEmailValidation({
          isValidating: false,
          isValid: false,
          message: result.error || 'Email validation failed',
          canInvite: false,
          userExists: result.userExists
        })
      }
    } catch (error) {
      console.error('Email validation error:', error)
      // A failure is not an answer: the next visit to this tab asks again.
      lastValidatedEmail.current = null
      setEmailValidation({
        isValidating: false,
        isValid: false,
        message: 'Failed to validate email',
        canInvite: false,
        userExists: null
      })
    }
  }

  // Debounced email validation
  useEffect(() => {
    if (activeTab === 0 && formData.email && companyId && formData.email !== lastValidatedEmail.current) {
      const timeoutId = setTimeout(() => {
        validateEmail(formData.email)
      }, 500) // 500ms delay

      return () => clearTimeout(timeoutId)
    }
  }, [formData.email, companyId, activeTab])

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
    // The link tab has no form: the invite form stays as it was for when they come back.
    if (newValue === LINK_TAB) return
    const invitationType = newValue === 0 ? 'email' : 'existing_user'
    onFormChange({
      ...formData,
      invitationType,
      selectedUser: invitationType === 'email' ? null : formData.selectedUser,
      email: invitationType === 'existing_user' ? '' : formData.email,
    })
  }

  const handleUserSelect = (user: any) => {
    if (user) {
      onFormChange({
        ...formData,
        selectedUser: user,
        email: user.email,
        invitationType: 'existing_user',
      })
    } else {
      onFormChange({
        ...formData,
        selectedUser: null,
        email: '',
        invitationType: 'existing_user',
      })
    }
  }

  const handlePermissionToggle = (permission: string) => {
    const newPermissions = formData.permissions.includes(permission)
      ? formData.permissions.filter(p => p !== permission)
      : [...formData.permissions, permission]
    
    onFormChange({
      ...formData,
      permissions: newPermissions,
    })
  }

  const handleSubmit = async () => {
    // Validate form data
    if (activeTab === 0 && !formData.email) {
      return // Email is required for email invitation
    }
    if (activeTab === 1 && !formData.selectedUser) {
      return // User selection is required for existing user invitation
    }

    // For email invitations, check if email is valid
    if (activeTab === 0 && !emailValidation.canInvite) {
      return // Email validation failed
    }

    setIsSubmitting(true)

    try {
      // Use the new invitation flow that handles user creation automatically
      if (activeTab === 0 && companyId) {
        const result = await CompanyService.inviteUserToCompanyAdvanced(companyId, {
          email: formData.email,
          role: inviteRole,
          department: formData.department,
          title: formData.title,
          permissions: formData.permissions,
          invitationType: 'email'
        })

        if (result.success) {
          trackEvent('APP_invite_sent', { invite_method: 'email', companyId }, 'Company')
          // Call the original onSubmit to handle UI updates
          onSubmit()
        } else {
          // Handle error - could show a toast or alert
          console.error('Invitation failed:', result.error)
          // For now, still call onSubmit to let parent handle the error
          onSubmit()
        }
      } else {
        // For existing user invitations, use the original flow (parent handles API)
        onSubmit()
      }
    } catch (error) {
      console.error('Error during invitation:', error)
      // Still call onSubmit to let parent handle the error
      onSubmit()
    } finally {
      setIsSubmitting(false)
    }
  }

  // Check if form is valid
  const isFormValid = () => {
    if (activeTab === 0) {
      return formData.email && 
             formData.email.trim() !== '' && 
             emailValidation.canInvite &&
             !emailValidation.isValidating
    }
    if (activeTab === 1) {
      return formData.selectedUser && !formData.selectedUser.isExistingMember
    }
    return false
  }

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <StyledDialogTitle>
        <AddIcon sx={{ color: 'primary.main', fontSize: '24px' }} />
        Invite User to Company
      </StyledDialogTitle>
      <StyledDialogContent>
        {/* Invitation Type Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            // Three icon tabs do not fit a narrow dialog: scroll instead of clipping the last.
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            textColor="primary"
            indicatorColor="primary"
            sx={{
              '& .MuiTab-root': {
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: 'primary.main',
                },
              },
            }}
          >
            <Tab 
              icon={<EmailIcon />} 
              label="Invite by Email" 
              iconPosition="start"
            />
            <Tab 
              icon={<PersonIcon />} 
              label="Add Existing User" 
              iconPosition="start"
            />
            {showLinkTab && (
              <Tab
                value={LINK_TAB}
                icon={<LinkIcon />}
                label="Invite link"
                iconPosition="start"
              />
            )}
          </Tabs>
        </Box>

        {onLinkTab ? (
          // Mounted only while this tab is shown, so the link is fetched when someone opens it.
          <WorkspaceInviteLinkCard
            companyId={companyId}
            createIfMissing={false}
            inviteRoles={rolesState}
          />
        ) : (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {/* User Information */}
            <Grid item xs={12}>
              <Box sx={{ 
                mb: 2, 
                p: 1.5, 
                bgcolor: 'action.hover',
                backgroundImage: 'none',
                borderRadius: 2, 
                border: '1px solid',
                borderColor: 'divider',
              }}>
                <Typography variant="h6" sx={{ 
                  mb: 1, 
                  color: 'text.primary', 
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <PersonIcon sx={{ color: 'primary.main', fontSize: '20px' }} />
                  User Information
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Enter the user details for the invitation
                </Typography>
              </Box>
            </Grid>
          
            {/* Email Tab Content */}
            {activeTab === 0 && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  margin="dense"
                  placeholder="Enter email address to invite new user"
                  error={emailValidation.isValid === false}
                  helperText={emailValidation.message || ''}
                  InputProps={{
                    endAdornment: emailValidation.isValidating ? (
                      <CircularProgress size={20} />
                    ) : emailValidation.isValid === true ? (
                      <CheckCircleIcon sx={{ color: 'success.main' }} />
                    ) : emailValidation.isValid === false ? (
                      <ErrorIcon sx={{ color: 'error.main' }} />
                    ) : null
                  }}
                />
              
                {/* Email validation status indicator */}
                {formData.email && (
                  <Box sx={{ 
                    mt: 1.5, 
                    p: 1.5, 
                    borderRadius: 1, 
                    backgroundImage: 'none',
                    bgcolor:
                      emailValidation.isValid === true
                        ? alpha(theme.palette.success.main, 0.1)
                        : emailValidation.isValid === false
                          ? alpha(theme.palette.error.main, 0.1)
                          : alpha(theme.palette.primary.main, 0.08),
                    border:
                      emailValidation.isValid === true
                        ? `1px solid ${alpha(theme.palette.success.main, 0.35)}`
                        : emailValidation.isValid === false
                          ? `1px solid ${alpha(theme.palette.error.main, 0.35)}`
                          : `1px solid ${alpha(theme.palette.primary.main, 0.28)}`,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {emailValidation.isValidating ? (
                        <>
                          <CircularProgress size={16} sx={{ color: 'primary.main' }} />
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                            Validating email...
                          </Typography>
                        </>
                      ) : emailValidation.isValid === true ? (
                        <>
                          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16 }} />
                          <Typography variant="body2" sx={{ color: 'success.dark', fontWeight: 500 }}>
                            {emailValidation.message}
                          </Typography>
                        </>
                      ) : emailValidation.isValid === false ? (
                        <>
                          <ErrorIcon sx={{ color: 'error.main', fontSize: 16 }} />
                          <Typography variant="body2" sx={{ color: 'error.dark', fontWeight: 500 }}>
                            {emailValidation.message}
                          </Typography>
                        </>
                      ) : null}
                    </Box>
                  </Box>
                )}
              </Grid>
            )}
          
            {/* Existing User Tab Content */}
            {activeTab === 1 && (
              <Grid item xs={12}>
                <UserSearchAutocomplete
                  value={formData.selectedUser}
                  onChange={handleUserSelect}
                  companyId={companyId}
                  label="Search Existing Users"
                  placeholder="Type to search for existing users..."
                  isGlobalAdmin={isGlobalAdmin}
                />
              </Grid>
            )}
          
            {/* Role and Department Information */}
            <Grid item xs={12}>
              <Box sx={{ 
                mb: 2, 
                p: 1.5, 
                bgcolor: 'action.hover',
                backgroundImage: 'none',
                borderRadius: 2, 
                border: '1px solid',
                borderColor: 'divider',
              }}>
                <Typography variant="h6" sx={{ 
                  mb: 1, 
                  color: 'text.primary', 
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <Box sx={{ 
                    width: 20, 
                    height: 20, 
                    borderRadius: '50%', 
                    bgcolor: 'primary.main',
                    backgroundImage: 'none',
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: 'primary.contrastText',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    R
                  </Box>
                  Role & Department Information
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Set the user's role and organizational details
                </Typography>
              </Box>
            </Grid>
          
            {canPickRole ? (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="dense">
                  <InputLabel id="invite-user-role-label">Role</InputLabel>
                  <Select
                    labelId="invite-user-role-label"
                    // A member's own role shows until the inviter picks one: it is what stays.
                    value={knownMember ? (pickedRole?.key ?? '') : selectedRoleKey}
                    displayEmpty={!!knownMember}
                    renderValue={
                      knownMember && !pickedRole
                        ? () => `${knownMemberRoleName} (current)`
                        : (value) => choices.find((r) => r.key === value)?.name ?? String(value)
                    }
                    onChange={(e) => setPickedRoleKey(String(e.target.value))}
                    label="Role"
                    data-testid="invite-role-picker"
                  >
                    {choices.map((role) => (
                      <MenuItem key={role.key} value={role.key}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ) : knownMember ? (
              <Grid item xs={12} md={6}>
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', mt: 2 }}
                  data-testid="invite-role-readonly"
                >
                  {`Already a member. Their role stays ${knownMemberRoleName}.`}
                </Typography>
              </Grid>
            ) : (
              // The role is not a choice here, but people should see what the invite gives.
              (defaultRoleName || rolesState.status === 'error') && (
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', mt: 2 }}
                    data-testid="invite-role-readonly"
                  >
                    {defaultRoleName
                      ? `Role: ${defaultRoleName}`
                      : "Could not load roles. The invite will use the company's default role."}
                  </Typography>
                </Grid>
              )
            )}
          
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Department"
                value={formData.department}
                onChange={(e) => handleInputChange('department', e.target.value)}
                margin="dense"
              />
            </Grid>
          
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                margin="normal"
              />
            </Grid>

            {/* Permissions Section */}
            <Grid item xs={12}>
              <Box sx={{ 
                mb: 2, 
                p: 1.5, 
                bgcolor: 'action.hover',
                backgroundImage: 'none',
                borderRadius: 2, 
                border: '1px solid',
                borderColor: 'divider',
              }}>
                <Typography variant="h6" sx={{ 
                  mb: 1, 
                  color: 'text.primary', 
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <Box sx={{ 
                    width: 20, 
                    height: 20, 
                    borderRadius: '50%', 
                    bgcolor: 'info.main',
                    backgroundImage: 'none',
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: 'info.contrastText',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    P
                  </Box>
                  Initial Permissions
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Select the initial permissions for this user
                </Typography>
              
                <Grid container spacing={1.5}>
                  {AVAILABLE_PERMISSIONS.map((permission) => (
                    <Grid item xs={12} md={6} key={permission}>
                      <Box sx={{ 
                        p: 1.5, 
                        borderRadius: 1, 
                        backgroundImage: 'none',
                        bgcolor: formData.permissions.includes(permission) 
                          ? alpha(theme.palette.info.main, 0.1) 
                          : 'background.paper',
                        border: '1px solid',
                        borderColor: formData.permissions.includes(permission) 
                          ? alpha(theme.palette.info.main, 0.35) 
                          : 'divider',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: formData.permissions.includes(permission) 
                            ? alpha(theme.palette.info.main, 0.14) 
                            : alpha(theme.palette.info.main, 0.06),
                          backgroundImage: 'none',
                          borderColor: alpha(theme.palette.info.main, 0.45),
                        }
                      }} 
                      onClick={() => handlePermissionToggle(permission)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <StyledSwitch
                            checked={formData.permissions.includes(permission)}
                            onChange={() => handlePermissionToggle(permission)}
                            size="small"
                          />
                          <Typography sx={{ 
                            color: 'text.primary', 
                            fontWeight: formData.permissions.includes(permission) ? 500 : 400,
                            fontSize: '0.875rem'
                          }}>
                            {permission.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          </Grid>
        )}
      </StyledDialogContent>
      <StyledDialogActions>
        <StyledButton onClick={onClose} variant="outlined">
          {onLinkTab ? 'Close' : 'Cancel'}
        </StyledButton>
        {!onLinkTab && (
          <StyledButton 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!isFormValid() || isSubmitting || rolesState.status === 'loading'}
            startIcon={isSubmitting ? <CircularProgress size={20} sx={{ color: 'primary.contrastText' }} /> : null}
          >
            {isSubmitting ? (
              'Processing...'
            ) : activeTab === 0 ? (
              emailValidation.userExists === false ? 'Create Account & Invite' : 'Invite User'
            ) : (
              'Add User to Company'
            )}
          </StyledButton>
        )}
      </StyledDialogActions>
    </StyledDialog>
  )
} 